/**
 * Code-driven game feel (spec.md §N.4): hit flash, knockback, camera shake, floating
 * damage text, hit-stop, pickup sparkle, and extract/descend pulses. The art carries
 * the readable motion; this layer makes even a 2-frame hit feel impactful.
 *
 * Everything is **clock-driven and pure**: effects are stored as `{ startedAt, duration }`
 * records and every getter computes its value from an absolute `now` (ms). The dungeon
 * stage's rAF loop calls the getters each frame; tests drive them with a fake clock. No
 * timers, no DOM. `update(now)` is optional housekeeping that prunes expired records.
 *
 * Reduced motion (spec.md §N; `prefers-reduced-motion`): positional juice — shake,
 * knockback, pulse displacement — is suppressed; non-vestibular feedback (flash,
 * floating numbers, hit-stop) is kept so combat still reads.
 */

export interface Vec2 {
  x: number;
  y: number;
}

interface FlashRecord {
  startedAt: number;
  durationMs: number;
}

interface KnockbackRecord {
  startedAt: number;
  durationMs: number;
  x: number;
  y: number;
}

interface ShakeRecord {
  startedAt: number;
  durationMs: number;
  intensity: number;
}

interface HitStopRecord {
  endsAt: number;
}

interface FloatingTextRecord {
  id: number;
  text: string;
  x: number;
  y: number;
  color: string;
  startedAt: number;
  durationMs: number;
}

interface SparkRecord {
  id: number;
  x: number;
  y: number;
  startedAt: number;
  durationMs: number;
}

interface PulseRecord {
  kind: string;
  startedAt: number;
  durationMs: number;
}

export interface FloatingTextView {
  id: number;
  text: string;
  x: number;
  y: number;
  color: string;
  /** 0 at spawn → 1 at expiry. */
  progress: number;
}

export interface SparkView {
  id: number;
  x: number;
  y: number;
  progress: number;
}

export interface PulseView {
  kind: string;
  progress: number;
}

export interface EffectsOptions {
  reducedMotion?: boolean;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export class EffectsController {
  private readonly reducedMotion: boolean;
  private flashes = new Map<string, FlashRecord>();
  private knockbacks = new Map<string, KnockbackRecord>();
  private shakeRec: ShakeRecord | null = null;
  private hitStopRec: HitStopRecord | null = null;
  private floatingTexts: FloatingTextRecord[] = [];
  private sparks: SparkRecord[] = [];
  private pulseRec: PulseRecord | null = null;
  private nextId = 1;

  constructor(options: EffectsOptions = {}) {
    this.reducedMotion = options.reducedMotion === true;
  }

  // ── hit flash ───────────────────────────────────────────────────────────────
  flash(entityId: string, { durationMs, now }: { durationMs: number; now: number }): void {
    this.flashes.set(entityId, { startedAt: now, durationMs });
  }

  /** Flash intensity 1 → 0 over the flash duration; 0 when none/expired. */
  getFlash(entityId: string, now: number): number {
    const rec = this.flashes.get(entityId);
    if (!rec) return 0;
    const t = clamp01((now - rec.startedAt) / rec.durationMs);
    if (t >= 1) return 0;
    return 1 - t;
  }

  // ── knockback ─────────────────────────────────────────────────────────────────
  knockback(
    entityId: string,
    { dirX, dirY, distance, durationMs, now }: { dirX: number; dirY: number; distance: number; durationMs: number; now: number },
  ): void {
    this.knockbacks.set(entityId, { startedAt: now, durationMs, x: dirX * distance, y: dirY * distance });
  }

  /** Knockback offset, full at impact and easing back to zero; zeroed under reduced motion. */
  getKnockback(entityId: string, now: number): Vec2 {
    if (this.reducedMotion) return { x: 0, y: 0 };
    const rec = this.knockbacks.get(entityId);
    if (!rec) return { x: 0, y: 0 };
    const t = clamp01((now - rec.startedAt) / rec.durationMs);
    if (t >= 1) return { x: 0, y: 0 };
    const ease = 1 - t;
    return { x: rec.x * ease, y: rec.y * ease };
  }

  // ── camera shake ──────────────────────────────────────────────────────────────
  shake({ intensity, durationMs, now }: { intensity: number; durationMs: number; now: number }): void {
    // A stronger shake overrides a weaker active one; otherwise keep the stronger.
    if (this.shakeRec && this.getShakeMagnitude(now) >= intensity) return;
    this.shakeRec = { startedAt: now, durationMs, intensity };
  }

  /** Linearly-decaying shake magnitude (px); 0 when expired or under reduced motion. */
  getShakeMagnitude(now: number): number {
    if (this.reducedMotion || !this.shakeRec) return 0;
    const t = (now - this.shakeRec.startedAt) / this.shakeRec.durationMs;
    if (t >= 1 || t < 0) return 0;
    return this.shakeRec.intensity * (1 - t);
  }

  /** Deterministic shake offset bounded by the current magnitude (no RNG → stable in tests). */
  getShake(now: number): Vec2 {
    const mag = this.getShakeMagnitude(now);
    if (mag === 0) return { x: 0, y: 0 };
    // High-frequency oscillation; phases chosen so x and y are out of step.
    return {
      x: Math.sin(now * 0.85) * mag,
      y: Math.cos(now * 0.97 + 1.7) * mag,
    };
  }

  // ── hit stop ────────────────────────────────────────────────────────────────────
  hitStop({ durationMs, now }: { durationMs: number; now: number }): void {
    const endsAt = now + durationMs;
    if (!this.hitStopRec || endsAt > this.hitStopRec.endsAt) {
      this.hitStopRec = { endsAt };
    }
  }

  isHitStopped(now: number): boolean {
    return this.hitStopRec !== null && now < this.hitStopRec.endsAt;
  }

  remainingHitStopMs(now: number): number {
    if (!this.hitStopRec) return 0;
    return Math.max(0, this.hitStopRec.endsAt - now);
  }

  // ── floating text ─────────────────────────────────────────────────────────────────
  floatingText(
    text: string,
    { x, y, durationMs, now, color = '#e8e0d2' }: { x: number; y: number; durationMs: number; now: number; color?: string },
  ): number {
    const id = this.nextId++;
    this.floatingTexts.push({ id, text, x, y, color, startedAt: now, durationMs });
    return id;
  }

  getFloatingTexts(now: number): FloatingTextView[] {
    const out: FloatingTextView[] = [];
    for (const t of this.floatingTexts) {
      const progress = (now - t.startedAt) / t.durationMs;
      if (progress >= 0 && progress < 1) {
        out.push({ id: t.id, text: t.text, x: t.x, y: t.y, color: t.color, progress });
      }
    }
    return out;
  }

  // ── sparkle ─────────────────────────────────────────────────────────────────────
  spark({ x, y, durationMs, now }: { x: number; y: number; durationMs: number; now: number }): number {
    const id = this.nextId++;
    this.sparks.push({ id, x, y, startedAt: now, durationMs });
    return id;
  }

  getSparks(now: number): SparkView[] {
    const out: SparkView[] = [];
    for (const s of this.sparks) {
      const progress = (now - s.startedAt) / s.durationMs;
      if (progress >= 0 && progress < 1) out.push({ id: s.id, x: s.x, y: s.y, progress });
    }
    return out;
  }

  // ── pulse (extract / descend) ─────────────────────────────────────────────────────
  pulse(kind: string, { durationMs, now }: { durationMs: number; now: number }): void {
    this.pulseRec = { kind, startedAt: now, durationMs };
  }

  getPulse(now: number): PulseView | null {
    if (!this.pulseRec) return null;
    const progress = (now - this.pulseRec.startedAt) / this.pulseRec.durationMs;
    if (progress < 0 || progress >= 1) return null;
    return { kind: this.pulseRec.kind, progress };
  }

  // ── housekeeping ──────────────────────────────────────────────────────────────────
  /** Total active timed records (floating texts + sparks), for tests/diagnostics. */
  activeCount(): number {
    return this.floatingTexts.length + this.sparks.length;
  }

  /** Prune expired records so they do not accumulate over a long run. */
  update(now: number): void {
    this.floatingTexts = this.floatingTexts.filter((t) => now - t.startedAt < t.durationMs);
    this.sparks = this.sparks.filter((s) => now - s.startedAt < s.durationMs);
    for (const [id, rec] of this.flashes) if (now - rec.startedAt >= rec.durationMs) this.flashes.delete(id);
    for (const [id, rec] of this.knockbacks) if (now - rec.startedAt >= rec.durationMs) this.knockbacks.delete(id);
    if (this.shakeRec && now - this.shakeRec.startedAt >= this.shakeRec.durationMs) this.shakeRec = null;
    if (this.pulseRec && now - this.pulseRec.startedAt >= this.pulseRec.durationMs) this.pulseRec = null;
    if (this.hitStopRec && now >= this.hitStopRec.endsAt) this.hitStopRec = null;
  }

  clear(): void {
    this.flashes.clear();
    this.knockbacks.clear();
    this.shakeRec = null;
    this.hitStopRec = null;
    this.floatingTexts = [];
    this.sparks = [];
    this.pulseRec = null;
  }
}
